/** Structured logger with in-memory buffer and downloadable log file */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  module: string;
  message: string;
}

/** Logger that writes to console and an in-memory buffer */
export class Logger {
  private static _buffer: LogEntry[] = [];
  private static _minLevel: LogLevel = LogLevel.DEBUG;
  private _module: string;

  constructor(module: string) {
    this._module = module;
  }

  static setMinLevel(level: LogLevel): void {
    Logger._minLevel = level;
  }

  static getBuffer(): readonly LogEntry[] {
    return Logger._buffer;
  }

  /** Download the log buffer as a text file */
  static downloadLog(): void {
    const text = Logger._buffer
      .map((e) => `[${e.timestamp}] [${LogLevel[e.level]}] [${e.module}] ${e.message}`)
      .join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rush-fever-7s-log-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  debug(message: string): void {
    this._log(LogLevel.DEBUG, message);
  }

  info(message: string): void {
    this._log(LogLevel.INFO, message);
  }

  warn(message: string): void {
    this._log(LogLevel.WARN, message);
  }

  error(message: string): void {
    this._log(LogLevel.ERROR, message);
  }

  private _log(level: LogLevel, message: string): void {
    if (level < Logger._minLevel) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      module: this._module,
      message,
    };
    Logger._buffer.push(entry);

    const prefix = `[${LogLevel[level]}] [${this._module}]`;
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(prefix, message);
        break;
      case LogLevel.INFO:
        console.info(prefix, message);
        break;
      case LogLevel.WARN:
        console.warn(prefix, message);
        break;
      case LogLevel.ERROR:
        console.error(prefix, message);
        break;
    }
  }
}
